import { get, cloneDeep } from 'lodash-es';
import { formatBudgetInitial } from '../budget_utils';

const MEMBER_ROLES = ['manager', 'user', 'viewer'];

export const types = {
  UPDATE_MEMBERS: 'UPDATE_MEMBERS',
  TRANSFORM_DIALOG_INITIAL_STATE: 'TRANSFORM_DIALOG_INITIAL_STATE',
};

const maybeAddMembersToMembersMap = (state, stateKey) => {
  const membersState = get(state, stateKey);

  if (membersState) {
    const membersToConsiderChanged = []

    for (const [role, memberIds] of Object.entries(membersState)) {
      state.membersMap[role] = new Set(memberIds);

      if (stateKey === 'preselected_members') {
        membersToConsiderChanged.push(...memberIds.map((id) => ({ user_id: id, selected: true, role })))
      }
    }

    state.changes.members = membersToConsiderChanged;
  }
};

export const membersMutations = {
  /**
   * Currenly the way we send the members data is as follows (assuming just one "select"):
   * - when some members added: send the list of added members
   * - when all members added: send the list of added members (in fact all members)
   * - when some members removed: send the list of selected members
   * - when all members removed: send empty list
   */
  [types.UPDATE_MEMBERS](state, { role: updatedRole, items }) {
    const otherRoles = MEMBER_ROLES.filter((role) => role !== updatedRole);
    const newRoleIds = new Set(items);

    // In some weird scenarios I noticed the membersMap is initialized with empty Maps,
    // Only when transpiled. This code accounts for that, as we always expect a Set.
    // NB: `state.membersMap[updatedRole]` is a Vuex-reactive Set (a Proxy). The ES2025 Set methods
    // (difference/intersection/…) require a real Set internal slot and throw "called on incompatible
    // receiver" on a Proxy, so we copy into a plain Set for the `.difference()` calls below.
    const currentMembers = state.membersMap[updatedRole] instanceof Map ? new Set() : new Set(state.membersMap[updatedRole] || []);
    const diffAdded = newRoleIds.difference(currentMembers);
    const diffRemoved = currentMembers.difference(newRoleIds);

    state.membersMap[updatedRole] = newRoleIds;

    if (items.length) {
      for (const memberId of items) {
        // Remove from the other roles' members if present
        otherRoles.forEach((otherRole) => {
          state.membersMap[otherRole].delete(memberId);
        });
      }
    }

    const members = Object.entries(state.membersMap).flatMap(([role, members]) =>
      [...members.values()].map((id) => ({ user_id: id, selected: true, role })),
    );
    
    const changedIds = new Set([
      ...(state.changes.members || []).map((member) => member.user_id),
      ...diffAdded,
    ])

    // This addresses scenarios like doing "clear all" and then adding a member
    const previouslyRemoved = new Set(
      (state.changes.members || []).filter(
        (member) => member.role === 'remove' && !diffAdded.has(member.user_id)
      ).map((member) => member.user_id)
    );
    const changedMembers = [
      ...(members || []).filter((member) => changedIds.has(member.user_id)),
      ...([...diffRemoved, ...previouslyRemoved]).map((memberId) => ({ user_id: memberId, selected: false, role: 'remove' }))
    ]

    state.project.members = members;
    state.changes.members = changedMembers;
  },
  [types.TRANSFORM_DIALOG_INITIAL_STATE](state) {
    const project = state.project;

    if (state.isNewForm) {
      state.project = cloneDeep(state.project);
      state.project.link_to_task = state.organization.linked ? state.organization.linked : null;
      if (state.project.link_to_task) {
        state.changes.link_to_task = state.project.link_to_task;
      }

      if (state.show_schedule_report) {
        state.project.schedule_weekly_report = state.active_projects_count < 6;
        state.changes.schedule_weekly_report = state.active_projects_count < 6;
      }

      state.changes.billable = state.project.billable;
      state.changes.monitor_user_activity = state.project.monitor_user_activity;
      state.changes.disable_client_idle_tracking = state.project.disable_client_idle_tracking;
      state.changes.disable_project_tracking = state.project.disable_project_tracking;

      maybeAddMembersToMembersMap(state, 'preselected_members');
    } else {
      if (!project.client_id && project.client) {
        state.project.client_id = project.client.id;
        delete state.project.client;
      }

      maybeAddMembersToMembersMap(state, 'project.members');
    }

    if (project.budget) {
      formatBudgetInitial(state.project.budget);
    }
    if (project.members_limits?.groups) {
      state.project.members_limits.groups.forEach((value) => formatBudgetInitial(value));
    }
  },
};
